import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { Pagination, SwipeableDrawer, useTheme } from '@mui/material';
import {
  Button,
  DatePicker,
  Dropdown,
  Flex,
  IconButton,
  Loader,
  Text,
} from '@components';
import moment from 'moment';
import { FormattedMessage, useIntl } from 'react-intl';
import { Config } from '@config/config';
import { useMemo, useState } from 'react';
import { useMediaQueries } from '@utils/mediaQueries/mediaQueries';
import {
  amountRangeMenuItems,
  headerCells,
} from './NomineeCustomerInvestments.consts';
import { useInvestmentsFilter } from './NomineeCustomerInvestments.hooks';
import {
  BackButton,
  BottomPanelButtonsContainer,
  TableContainer,
} from './NomineeCustomerInvestments.styles';
import { CancelIcon } from '@pages/WrapperPages/Menu/Menu.styles';
import { EnhancedTableHead, Order } from '@components/Table/EnhancedTableHead';
import { getComparator } from '@utils/table/tableUtils';
import {
  useInvestmentAttributionsQuery,
  useInvestmentsQuery,
} from '@features/api/nomineeInvestmentsSlice';
import {
  NomineeInvestment,
  NomineeInvestmentStatus,
} from '@features/api/response/NomineeInvestment';
import { NomineeCustomer } from '@features/api/response/NomineeCustomer';
import { NomineeCustomerInvestment } from './NomineeCustomerInvestments.types';
import { NewInvestmentPanel } from '@components/NewInvestmentPanel/NewInvestmentPanel';
import { Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export interface NomineeCustomerInvestmentsProps {
  customer?: NomineeCustomer;
}

const rowsPerPage = 8;
export default function NomineeCustomerInvestments({
  customer,
}: NomineeCustomerInvestmentsProps) {
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();
  const { sm: isMobile } = useMediaQueries();

  const { data: initialInvestments, isLoading: isLoadingInvestments } =
    useInvestmentsQuery(undefined, { refetchOnMountOrArgChange: true });
  const { data: initialAttributions, isLoading: isLoadingAttributions } =
    useInvestmentAttributionsQuery(
      { nomineeCustomerId: customer?.id },
      {
        refetchOnMountOrArgChange: true,
        skip: !customer?.id,
      }
    );

  const customerInvestments = useMemo(() => {
    const investmentsById = initialInvestments?.reduce((acc, investment) => {
      return { ...acc, [investment.transactionId]: investment };
    }, {} as Record<number, NomineeInvestment>);

    if (!investmentsById) return [];
    return initialAttributions?.map((attribution) => {
      return {
        id: attribution.id,
        transactionId: attribution.transactionId,
        productName: investmentsById[attribution.transactionId].productName,
        amount: attribution.amount,
        shares: attribution.shares,
        investmentDate:
          investmentsById[attribution.transactionId].investmentDate,
        status: investmentsById[attribution.transactionId].status,
      } as NomineeCustomerInvestment;
    });
  }, [initialAttributions, initialInvestments]);

  const {
    values,
    handlers: {
      handleProductFilterChange,
      handleInvestmentDateFilterChange,
      handleAmountRangeFilterChange,
      handleSearchButtonClick,
      handleClearButtonClick,
    },
  } = useInvestmentsFilter(customerInvestments ?? []);
  const {
    availableProducts,
    filterCriteria,
    filteredData,
    isSearchButtonEnabled,
  } = values;

  const filtersLength = Object.values(filterCriteria ?? {}).length;

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] =
    useState<keyof NomineeCustomerInvestment>('investmentDate');
  const [page, setPage] = useState(1);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

  const [editingInvestmentAttribution, setEditingInvestmentAttribution] =
    useState<NomineeCustomerInvestment>();
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  const handleRequestSort = (
    _e: unknown,
    property: keyof NomineeCustomerInvestment
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_e: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleEditInvestmentAttribution = (
    investment: NomineeCustomerInvestment
  ) => {
    setEditingInvestmentAttribution(investment);
    setIsSidePanelOpen(true);
  };

  const handleCloseSidePanel = () => {
    setIsSidePanelOpen(false);
    setEditingInvestmentAttribution(undefined);
  };

  const renderFilters = () => (
    <>
      <Box flex={1}>
        <Dropdown
          label={intl.formatMessage({
            id: 'portfolio.transactions.product',
          })}
          menuItemData={availableProducts}
          onChange={handleProductFilterChange}
          value={filterCriteria?.productName ?? ''}
          defaultValue={filterCriteria?.productName ?? ''}
          menuAutoWidth
        />
      </Box>
      <DatePicker
        label={intl.formatMessage({ id: 'crm.investments.investmentDate' })}
        mobileActions={['accept']}
        disableFuture
        showToolbar={false}
        value={filterCriteria?.investmentDate ?? null}
        inputFormat='DD/MM/YYYY'
        minDate={filterCriteria?.investmentDate}
        onChange={handleInvestmentDateFilterChange}
      />

      <Box flex={1}>
        <Dropdown
          label={intl.formatMessage({
            id: 'portfolio.transactions.amountRange',
          })}
          menuItemData={amountRangeMenuItems}
          value={filterCriteria?.amountRange ?? ''}
          onChange={handleAmountRangeFilterChange}
          menuAutoWidth
        />
      </Box>
    </>
  );

  if (!filteredData) return <></>;

  const investmentsCount = filteredData.length > 0 ? filteredData.length : 0;
  const totalPages = Math.ceil(investmentsCount / rowsPerPage);

  return (
    <Flex
      flex={1}
      width='100%'
      flexDirection='column'
      data-testid='investments-container'
    >
      <Loader isLoading={isLoadingInvestments || isLoadingAttributions} />
      {!isMobile ? (
        <>
          <Flex>
            <BackButton variant='tertiary' onClick={handleClearButtonClick}>
              <FormattedMessage id='misc.clearAll' />
            </BackButton>
          </Flex>
          <Flex gap={6} alignItems='center' marginBottom={10}>
            {renderFilters()}
            <Flex>
              <Button
                variant='primary'
                sx={{ height: 50 }}
                disabled={!filtersLength || !isSearchButtonEnabled}
                onClick={handleSearchButtonClick}
              >
                <FormattedMessage id='misc.search' />
              </Button>
            </Flex>
          </Flex>
        </>
      ) : (
        <Flex width='100%' marginY={10}>
          <Button
            variant='secondary'
            onClick={() => setIsBottomSheetOpen(true)}
            fullWidth
            sx={{ height: 45 }}
          >
            {`${intl.formatMessage({ id: 'portfolio.transactions.filter' })} ${
              filtersLength ? `(${filtersLength})` : ''
            }`}
          </Button>
        </Flex>
      )}
      <TableContainer>
        <Flex overflow='auto'>
          <Table aria-labelledby='tableTitle'>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              headerCells={headerCells}
            />
            <TableBody>
              {filteredData.length ? (
                filteredData
                  ?.slice()
                  .sort((a, b) => {
                    const result = getComparator<
                      keyof NomineeCustomerInvestment
                    >(order, orderBy)(a, b);
                    const equalValuesResult = 0;
                    // If the values are equal we additionally sort on the ID
                    if (result === equalValuesResult) {
                      const idComparisonResult = getComparator<
                        keyof NomineeCustomerInvestment
                      >(order, 'id')(a, b);
                      return idComparisonResult;
                    }

                    return result;
                  })
                  .slice(
                    (page - 1) * rowsPerPage,
                    (page - 1) * rowsPerPage + rowsPerPage
                  )
                  .map((row, index) => {
                    const labelId = `enhanced-table-checkbox-${row.id}`;
                    return (
                      <TableRow hover tabIndex={-1} key={`${row.id}`}>
                        <TableCell
                          component='th'
                          id={labelId}
                          scope='row'
                          padding='normal'
                        >
                          {row.id}
                        </TableCell>
                        <TableCell>{row.productName}</TableCell>
                        <TableCell align='left'>
                          <Button
                            variant='tertiary'
                            onClick={() =>
                              navigate(`/crm/investments/${row.transactionId}`)
                            }
                          >
                            {row.transactionId}
                          </Button>
                        </TableCell>
                        <TableCell align='center'>
                          {row.status !== NomineeInvestmentStatus.Invested
                            ? '-'
                            : moment(row.investmentDate, 'YYYY-MM-DD').format(
                                'DD/MM/YYYY'
                              )}
                        </TableCell>
                        <TableCell align='right'>
                          {intl.formatNumber(row.amount, {
                            style: 'currency',
                            currency: Config.currency.code,
                            maximumFractionDigits: 2,
                            notation: 'standard',
                          })}
                        </TableCell>
                        <TableCell align='right'>{row.shares}</TableCell>
                        <TableCell align='right'>
                          <IconButton
                            aria-label='edit'
                            color='success'
                            iconBoxWidth={12}
                            iconBoxHeight={12}
                            icon={Edit}
                            onClick={() => handleEditInvestmentAttribution(row)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align='center'>
                    <Text
                      variant='h5'
                      fontWeight='fontWeightMedium'
                      sx={{ color: theme.palette.grey[700] }}
                    >
                      <FormattedMessage id='misc.noResults' />
                    </Text>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Flex>
        <Flex width='100%' justifyContent='center' padding={8}>
          <Pagination
            color='primary'
            shape='rounded'
            count={totalPages}
            page={page}
            onChange={handleChangePage}
          />
        </Flex>
      </TableContainer>
      {isMobile ? (
        <SwipeableDrawer
          anchor='bottom'
          open={isBottomSheetOpen}
          onClose={() => setIsBottomSheetOpen(false)}
          onOpen={() => setIsBottomSheetOpen(true)}
          keepMounted={false}
          PaperProps={{
            sx: {
              height: '90vh',
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
            },
          }}
        >
          <Box padding={10}>
            <Flex flex={1} alignItems='center' justifyContent='space-between'>
              <Text variant='h4' fontWeight='fontWeightMedium'>
                <FormattedMessage id='portfolio.transactions.selectFilters' />
              </Text>
              <IconButton
                data-testid='close-button'
                size='small'
                iconBoxHeight={25}
                iconBoxWidth={25}
                icon={() => <CancelIcon color='info' />}
                onClick={() => setIsBottomSheetOpen(false)}
              />
            </Flex>
            <Flex marginTop={18} gap={10} flexDirection='column'>
              {renderFilters()}
            </Flex>
            <BottomPanelButtonsContainer>
              <Button
                variant='secondary'
                sx={{ flex: 1, height: 45 }}
                onClick={handleClearButtonClick}
              >
                <FormattedMessage id='misc.clear' />
              </Button>
              <Button
                sx={{ flex: 1, height: 45 }}
                disabled={!filtersLength || !isSearchButtonEnabled}
                onClick={() => {
                  handleSearchButtonClick();
                  setIsBottomSheetOpen(false);
                }}
              >
                {`${intl.formatMessage({
                  id: 'misc.apply',
                })} ${filtersLength ? `(${filtersLength})` : ''}`}
              </Button>
            </BottomPanelButtonsContainer>
          </Box>
        </SwipeableDrawer>
      ) : null}

      <NewInvestmentPanel
        isOpen={isSidePanelOpen}
        onClose={handleCloseSidePanel}
        customer={customer}
        investmentAttribution={editingInvestmentAttribution}
      />
    </Flex>
  );
}
